import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuditsApi, CollectingOperationStatusDto, CollectingOperationsSummaryDto } from '@buspro/web/apis';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { lastValueFrom } from 'rxjs';

/** Nothing new for this long means somebody should look at the bus, not just wait. */
const STALLED_AFTER_MINUTES = 30;

/** How many rows the widget shows before deferring to the full list. */
const PREVIEW_ROWS = 5;

interface AgeBucket {
  key: string;
  label: string;
  count: number;
  /** Long waits are the ones worth pulling the eye. */
  urgent: boolean;
}

/**
 * "Operations Awaiting Media" — the stuck-operations surface.
 *
 * Media gating has no timeout and never promotes with gaps, which is correct but means a
 * trip whose footage never completes waits forever and silently. The age breakdown is the
 * point of the widget: a trip an hour old is the system working, and one three days old is
 * a bus nobody has looked at.
 *
 * Shares its query key with the collecting panel on the audit queue, so opening both costs
 * one request.
 */
@Component({
  selector: 'buspro-admin-awaiting-media-widget',
  standalone: true,
  imports: [CommonModule, RouterModule, NzIconModule, NzToolTipModule],
  templateUrl: './awaiting-media-widget.component.html',
})
export class AwaitingMediaWidgetComponent {
  private auditsApi = inject(AuditsApi);

  collectingQuery = injectQuery(() => ({
    queryKey: ['operations-collecting-media'],
    queryFn: () => lastValueFrom(this.auditsApi.auditsControllerGetCollectingOperations()),
    refetchInterval: 60000,
    placeholderData: (previousData: CollectingOperationsSummaryDto | undefined) => previousData,
  }));

  total = computed(() => this.collectingQuery.data()?.total ?? 0);
  isFirstLoad = computed(() => this.collectingQuery.isPending() && !this.collectingQuery.data());

  /** Oldest first — the server already orders by collectingStartedAt. */
  previewRows = computed(() => this.collectingQuery.data()?.operations.slice(0, PREVIEW_ROWS) ?? []);
  overflowCount = computed(() => Math.max(0, this.total() - this.previewRows().length));

  /**
   * Buckets in age order, with the empty leading ones dropped: when everything is under an
   * hour there is no reason to show four zeroes, and when something is three days old that
   * bucket should not be competing with them for attention.
   */
  ageBuckets = computed<AgeBucket[]>(() => {
    const b = this.collectingQuery.data()?.ageBuckets;
    if (!b) return [];
    return [
      { key: 'under1h', label: '< 1h', count: b.under1h, urgent: false },
      { key: 'oneToSixH', label: '1–6h', count: b.oneToSixH, urgent: false },
      { key: 'sixToTwentyFourH', label: '6–24h', count: b.sixToTwentyFourH, urgent: true },
      { key: 'oneToThreeDays', label: '1–3d', count: b.oneToThreeDays, urgent: true },
      { key: 'overThreeDays', label: '> 3d', count: b.overThreeDays, urgent: true },
    ].filter((bucket) => bucket.count > 0);
  });

  /** Trips old enough that waiting longer is not the answer. */
  urgentCount = computed(() => {
    const b = this.collectingQuery.data()?.ageBuckets;
    if (!b) return 0;
    return b.sixToTwentyFourH + b.oneToThreeDays + b.overThreeDays;
  });

  minutesSinceLastClip(op: CollectingOperationStatusDto): number | null {
    if (!op.lastClipAt) return null;
    return Math.max(0, Math.round((Date.now() - new Date(op.lastClipAt).getTime()) / 60000));
  }

  isStalled(op: CollectingOperationStatusDto): boolean {
    const since = this.minutesSinceLastClip(op);
    if (since === null) return (op.ageMinutes ?? 0) >= STALLED_AFTER_MINUTES;
    return since >= STALLED_AFTER_MINUTES;
  }

  /**
   * The single most useful thing to say about a stuck row, in priority order: a bus that is
   * off explains everything else, so it wins over "nothing arriving", which in turn beats
   * simply being incomplete.
   */
  diagnosis(op: CollectingOperationStatusDto): { label: string; tone: 'error' | 'warning' | 'neutral' } {
    // Given up on comes first: every other line here says "wait", and this one is the only
    // one that says "waiting will not help". Showing "quiet 3h" over an abandoned recovery
    // would read as patience being warranted when it is not.
    if (op.recoveryAbandoned > 0) {
      return { label: `recovery gave up on ${op.recoveryAbandoned}`, tone: 'error' };
    }
    if (op.busOnline === false) {
      const forMinutes = op.busOfflineForMinutes;
      return {
        label: forMinutes === null ? 'bus offline' : `bus offline ${this.humanMinutes(forMinutes)}`,
        tone: 'error',
      };
    }
    // Actively being chased is NOT the same as stuck — it is the system working, and saying
    // "quiet 20m" about a trip we are re-asking for every few minutes reads as neglect.
    if (op.recoveryPending > 0) {
      const tries = op.recoveryAttempts;
      return {
        label: `re-asking bus for ${op.recoveryPending}${tries ? ` (try ${tries + 1})` : ''}`,
        tone: 'warning',
      };
    }
    if (this.minutesSinceLastClip(op) === null) return { label: 'no footage yet', tone: 'error' };
    if (this.isStalled(op))
      return { label: `quiet ${this.humanMinutes(this.minutesSinceLastClip(op))}`, tone: 'warning' };
    return { label: 'arriving', tone: 'neutral' };
  }

  humanMinutes(total: number | null): string {
    if (total === null || total < 1) return 'just now';
    if (total < 60) return `${total}m`;
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  }

  trackById(_index: number, op: CollectingOperationStatusDto): string {
    return op.operationId;
  }
}
